import axios from "axios";
import {
  appendAuthInterceptor,
  requestWrapper,
} from "../../Common/serviceFactory";

const axiosContactsInstance = axios.create({
  baseURL: process.env.REACT_APP_REQUEST_CONTACT,
});
const axiosApplicantsInstance = axios.create({
  baseURL: process.env.REACT_APP_REQUEST_APPLICANTS,
});

const axiosPublicInstance = axios.create({
  baseURL: process.env.REACT_APP_REQUEST_PUBLIC,
});

const axiosCMSInstance = axios.create({
  baseURL: process.env.REACT_APP_CMS_REQUEST_BASE_URL,
});

appendAuthInterceptor(axiosApplicantsInstance);
appendAuthInterceptor(axiosContactsInstance);

const service = {
  getMediaUrl(path) {
    return `${process.env.REACT_APP_CMS_REQUEST_BASE_URL}media/${path}`;
  },
  async getContent(query) {
    const response = await requestWrapper(
      axiosCMSInstance.post(`api/graphql`, { query })
    );
    if (!response.error) {
      return response.data;
    }
  },
  async GetFaqs() {
    const query = `{fAQ {category answer question}}`;
    const result = await service.getContent(query);
    return result.fAQ;
  },
  async getTitleAndHtml(slug) {
    const query = `{
        titleandHTML(where: {alias: {alias: "${slug}"}}) {
            body {
                html
            }
            displayText
            subtitle
        }
    }`;
    const result = await service.getContent(query);
    const content = result.titleandHTML[0];
    if (!content)
      throw new Error(`titleandHTML with alias ${slug} was not found`);
    // direct images to apim
    content.body.html = content.body.html.replace(
      /<img src="\/media/g,
      `<img src="${process.env.REACT_APP_CMS_REQUEST_BASE_URL}media`
    );
    
    // direct links to apim
    content.body.html = content.body.html.replace(
      /<a href="\/media/g,
      `<a href="${process.env.REACT_APP_CMS_REQUEST_BASE_URL}media`
    );
    return {
      title: content.displayText,
      body: content.body,
      subtitle: content.subtitle,
    };
  },
  async getSimpleText(slug) {
    const query = `{plainText(where: {alias: "${slug}"}) {text}}`;
    const result = await service.getContent(query);
    const content = result.plainText[0];
    return content.text;
  },
  async getTemporaryMessage(slug) {
    const query = `{temporaryMessage(where: {alias: "${slug}"}) {from message to}}`;
    const result = await service.getContent(query);
    const content = result.temporaryMessage[0];
    const from = new Date(content.from);
    const to = new Date(content.to);
    const now = new Date();
    if ((from === null || from <= now) && (to === null || to >= now)) {
      return content.message;
    }
    return null;
  },
  async getImagesCollection(slug) {
    const query = `{ imagesCollection(where: {alias: "${slug}"}) {images {paths}}}`;
    const result = await service.getContent(query);
    const content = result.imagesCollection[0];
    return content.images.paths.map(service.getMediaUrl);
  },
  async getQuickStat(slug) {
    const query = `{quickstat(where: {alias: "${slug}"}) {displayText value}}`;
    const result = await service.getContent(query);
    const content = result.quickstat[0];
    return content;
  },
  async getContact() {
    return await requestWrapper(axiosContactsInstance.get(`contact/get`));
  },
  async agreeToTerms() {
    return await requestWrapper(
      axiosContactsInstance.get(`contact/termsagreed`)
    );
  },
  async getProposal() {
    return await requestWrapper(
      axiosApplicantsInstance.get(`applicant/proposal`)
    );
  },
  async register(request) {
    return await requestWrapper(
      axiosCMSInstance.post(`api/account/applicant/register`, request)
    );
  },
  async createProposal(proposal) {
    return await requestWrapper(
      axiosPublicInstance.post(`applicant/create`, proposal)
    );
  },
  async getAllInstitutions() {
    return await requestWrapper(
      axiosPublicInstance.get(`utility/institutions`)
    );
  },
  async GetProgramTypes() {
    return await requestWrapper(
      axiosPublicInstance.get(`utility/programs`)
    );
  },
  async GetRegistrationPrograms() {
    return await requestWrapper(
      axiosPublicInstance.get(`utility/registrationPrograms`)
    );
  },
  async SubmitContactUs(request) {
    return await requestWrapper(
      axiosPublicInstance.post(`/utility/contactus`, request)
    );
  },
  async GetContactTitles() {
    return await requestWrapper(
      axiosPublicInstance.get(`/utility/options/contactTitles`)
    );
  },
  async GetBudgetCodes() {
    return await requestWrapper(
      axiosPublicInstance.get(`/utility/options/budgetCodes`)
    );
  },
  async getResearchAreas() {
    return await requestWrapper(axiosPublicInstance.get(`utility/areas`));
  },
  async getResearchSubAreas(props) {
    const { areaId } = props;
    return await requestWrapper(
      axiosPublicInstance.get(`utility/subareas/${areaId}`)
    );
  },
  async getFormsFiles(props) {
    const { folderName } = props;
    return await requestWrapper(
      axiosPublicInstance.get(`utility/files?path=${folderName}`)
    );
  },
  async downloadFiles(props) {
    let { fullPath, fileName } = props;
    const filePath = `utility/files/${fullPath}`;
    const data = await requestWrapper(
      axiosPublicInstance.get(filePath, {
        params: {
          cacheBustTimestamp: Date.now(), // prevents IE cache problems on re-download
        },
        responseType: "blob",
        headers: {
          Accept: "application/octet-stream",
        },
      })
    );

    if (!(data instanceof Blob)) {
      return;
    }

    if (!fileName) fileName = fullPath.replace(/^.*[\\\\\\/]/, "");

    const blob = new Blob([data]);
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `${fileName}`;
    link.click();
  },
};

export default service;
